// Old WordPress page slugs still linked from blog posts and other sites.
//
// Each target is the redirect the WordPress site itself served (read from the
// WordPress staging copy, staging-thriverestorehair.kinsta.cloud, on 30 Sep
// 2026), so search engines see the same destination they learned before the
// migration. The three slugs WordPress no longer had are marked "no WP target"
// and point to the closest live page.

// Old WordPress slugs that answer 404 (Cloudflare analytics, 3 to 6 Oct 2026).
// server.ts answers these before the router (see legacy-path-redirects.ts), so
// /about and /about/ both get one 301. LEGACY_PAGES also holds them, so blog
// links and /llms.txt use the same target.
// Women's pages taken off the site on the business's decision (Brian Polis,
// 12 Oct 2026: RESTORE markets hair transplants to men only, and Google Ads
// must not expand to women's pages; confirmed in writing on 13 Oct 2026).
// /thairapy/ and /quick-quote-1-female/ (the women's quote form) were route
// files. The six posts are still rows in Supabase (blog_posts): nothing was
// deleted there.
// server.ts answers these before the router (see legacy-path-redirects.ts),
// so /female-hair-loss and /female-hair-loss/ both get one 301 with the query
// string kept. The slugs are also in REDIRECTED_POST_SLUGS (blog-listing.ts),
// which keeps the posts out of the blog listing, the sitemap and the related
// articles. Each target answered 200 with no redirect on restorehair.com on
// 12 Oct 2026. To bring a page back, remove its line here and there.
export const RETIRED_WOMENS_PAGES: Record<string, string> = {
  thairapy: "/non-surgical/",
  "female-hair-loss": "/non-surgical/", // post about minoxidil for women
  "ludwig-scale": "/what-is-the-norwood-scale/", // the hair loss scale page that stays
  "hair-loss-in-women-exploring-female-pattern-baldness": "/hair-loss/",
  "why-women-lose-hair": "/hair-loss/",
  "hair-loss-in-women": "/hair-loss/",
  "anti-aging-strategies-for-women": "/hair-loss/",
  // The women's quote form. Saved links and bookmarks go to the standard
  // quote form. The server code still accepts a lead that names this page
  // (formTypeFromPath, the ludwig field), so a tab left open on the old form
  // can still send its lead.
  "quick-quote-1-female": "/quick-quote-part-1/",
};

// Blog tag archives named for women's topics (rows in Supabase blog_tags,
// not deleted). They were noindex and no page linked to them. Each goes to
// the blog index, and so do the old /blog/tag/ and /hair-loss-blog/tag/ forms
// of the same URL, which otherwise redirect to /tag/<slug>/ first.
// /tag/transgender/ stays: its one post stays.
const RETIRED_WOMENS_TAGS = [
  "female-hair-loss",
  "female-pattern-baldness",
  "hair-loss-in-women",
  "hair-transplant-for-women",
  "ludwig-hair-loss-scale",
];
export const RETIRED_WOMENS_TAG_PATHS: Record<string, string> = Object.fromEntries(
  RETIRED_WOMENS_TAGS.flatMap((slug) =>
    ["/tag/", "/blog/tag/", "/hair-loss-blog/tag/"].map((prefix) => [
      `${prefix}${slug}`,
      "/hair-loss-blog/",
    ]),
  ),
);

export const STAGING_SLUGS: Record<string, string> = {
  // Each target is the 301 the WordPress staging copy
  // (staging-thriverestorehair.kinsta.cloud) served. Each target
  // answered 200 with no redirect on restorehair.com on 6 Oct 2026.
  "1": "/10-ways-actually-make-hair-grow/",
  about: "/about-us/",
  career: "/careers/",
  "ask-a-doctor": "/contact-us/",
  "coach-deion-sanders-i-got-my-barber-back": "/deion-sanders-hair-transplant/",
  "coach-deion-sanders-i-need-the-reality": "/deion-sanders-hair-transplant/",
  "coach-deion-sanders-my-confidence-in-dr-james-harris": "/deion-sanders-hair-transplant/",
  "coach-deion-sanders-my-hair-loss-story": "/deion-sanders-hair-transplant/",
  "coach-deion-sanders-no-shave-fue": "/deion-sanders-hair-transplant/",
  "coach-deion-sanders-q-a-with-restore": "/deion-sanders-hair-transplant/",
  "coach-deion-sanders-rates-his-hair-after-restore": "/deion-sanders-hair-transplant/",
  "coach-deion-sanders-recommends-restore": "/deion-sanders-hair-transplant/",
  "coach-deion-sanders-restore-story": "/deion-sanders-hair-transplant/",
  "deion-sanders-top-10-essentials": "/deion-sanders-hair-transplant/",
  "primetime-vs-hairline": "/deion-sanders-hair-transplant/",
  "dr-james-harris": "/doctors/dr-james-harris/",
  "fue-hair-transplant": "/fue-hair-transplant-care-when-to-wash-your-hair/",
  "researchers-discover-genes-associated-human-hair-growth": "/genes-human-hair-growth/",
  "platelet-rich-plasma-hair-loss": "/hair-survival-program/",
  "platelet-rich-plasma-therapy-for-hair-loss": "/hair-survival-program/",
  "hair-transplant-overview": "/hair-transplant-an-effective-way-of-hair-restoration/",
  faqs: "/hair-transplant-faqs/",
  "how-many-grafts-do-i-need": "/how-many-grafts/",
  "is-it-too-late-for-a-hair-transplant-hair-sciences-center":
    "/is-it-too-late-for-a-hair-transplant/",
  "locations-1": "/locations/",
  dist: "/locations/district-of-columbia/",
  atlanta: "/locations/georgia/atlanta/",
  new: "/locations/new-jersey/",
  chicago: "/locations/restore-chicago/",
  "landing-page-chicago": "/locations/restore-chicago/",
  denver: "/locations/restore-denver/",
  utah: "/locations/utah/",
  media: "/media-outlet/",
  "a-leading-topical-hair-enhancement-product":
    "/non-surgical-hair-loss-treatments-at-restore-hair/",
  "finasteride-propecia-proscar": "/non-surgical/",
  "hair-transplant-surgery": "/procedure/",
  pro: "/procedure/",
  "ryne-sandberg": "/results/ryne-sandberg/",
  "women-non-surgical": "/non-surgical/", // staging target /thairapy/ was retired on 12 Oct 2026
  "advanced-hair-transplant": "/why-restore/",
  // No WordPress target (staging answers 404 too). The target is an inference
  // from the slug: financing words go to pricing, team words to the doctors
  // page, job words to careers.
  "careers-home": "/careers/",
  jobs: "/careers/",
  "join-us": "/careers/",
  "work-with-us": "/careers/",
  contact_us: "/contact-us/",
  contactus: "/contact-us/",
  "reach-us": "/contact-us/",
  leadership: "/doctors/",
  "our-team": "/doctors/",
  staff: "/doctors/",
  team: "/doctors/",
  carecredit: "/pricing/",
  finance: "/pricing/",
  "financing-options": "/pricing/",
  financing: "/pricing/",
  "patient-financing": "/pricing/",
  patientfi: "/pricing/",
  "pay-over-time": "/pricing/",
  "payment-options": "/pricing/",
  "payment-plan": "/pricing/",
  "payment-plans": "/pricing/",
  "womens-hair-restoration": "/non-surgical/", // was /thairapy/ until 12 Oct 2026
};

const LEGACY_PAGES: Record<string, string> = {
  ...STAGING_SLUGS,
  ...RETIRED_WOMENS_PAGES,
  "hair-transplant-surgeon-physician-bio": "/doctors/dr-james-harris/",
  "physician-cv-james-harris": "/doctors/dr-james-harris/",
  "hair-replacement-client-bill-of-rights": "/doctors/dr-james-harris/",
  "hair-transplant-reviews": "/reviews/",
  contact: "/contact-us/",
  "non-surgical-hair-restoration": "/non-surgical/",
  "minoxidil-rogaine": "/non-surgical/",
  fue: "/fue-hair-transplant-care-when-to-wash-your-hair/",
  "no-shave-fue": "/no-shave-fue-hair-transplant/",
  // These four pointed at the women's pages until 12 Oct 2026. They now go
  // straight to the page those redirect to, so no chain is left.
  "female-pattern-baldness": "/hair-loss/",
  "female-pattern-baldness-2": "/hair-loss/",
  "hairline-transplant-for-women": "/procedure/",
  "causes-of-hair-loss-in-women": "/hair-loss/",
  artas: "/artas-work-curly-hair/",
  "hair-loss-treatment": "/procedure/",
  fuht: "/procedure/",
  "follicular-unit-extraction": "/procedure/",
  "medical-hair-restoration": "/procedure/",
  "hairline-transplant": "/procedure/",
  "hair-loss-in-men": "/causes-of-hair-loss/",
  "causes-of-hair-loss-in-men": "/causes-of-hair-loss/",
  "s-a-f-e-system": "/history-of-fue/",
  "hsc-difference": "/locations/restore-denver/",
  "strip-method": "/locations/restore-denver/",
  "hair-transplant-before-and-after": "/results/",
  "erin-parisi": "/results/",
  "what-to-expect": "/what-to-expect-at-your-hair-restoration-consultation/",
  consultation: "/downloads/restore_presentation_mobile_v26-1.pdf",
  // On WordPress, /brochure/ was a bare page showing the Nationwide brochure
  // PDF full width (thriverestorehair.kinsta.cloud, read on 1 Nov 2026). That
  // PDF now lives here, so the old link goes straight to it. RESTORE's
  // Salesforce drip emails (intro, Day10, Day17) still link to /brochure/ and
  // /Brochure, both with UTMs; the 301 keeps the query string.
  brochure: "/downloads/RESTORE-Hair-Brochure.pdf",
  "privacy-terms": "/privacy-policy/",
  "hair-transplant-cost": "/the-true-hair-restoration-cost/",
  "hair-transplant": "/hair-transplant-an-effective-way-of-hair-restoration/",
  "hair-loss-hat-collection": "/hat-collection/",
  "correcting-previous-hair-surgeries": "/correcting-previous-hair-transplant-surgeries/",
  "genetics-and-hair-loss": "/baldness-gene-come-mother-father/", // was the genetics post, merged 17 Oct 2026
  // no WP target
  "how-many-grafts-do-i-need-calculator": "/how-many-grafts/",
  "stars-with-hair-plugs": "/famous-people-with-hair-transplants/",
  // 404 pages that still had Google impressions from 1 to 28 Sep 2026
  // (Search Console). WordPress had no redirect for them either.
  "genes-and-hair-loss-who-do-you-inherit-it-from": "/baldness-gene-come-mother-father/",
  "no-shave-hair-transplant": "/no-shave-fue-hair-transplant/",
  "does-a-hair-transplant-hurt-is-an-fue-hair-transplant-painful": "/does-a-hair-transplant-hurt/",
  "the-norwood-scale": "/what-is-the-norwood-scale/",
  "hair-loss-braids": "/braiding-can-make-your-hair-fall-out/",
  // Old URLs that other sites still link to (Ahrefs referring domains, all
  // time, read on 1 Oct 2026). Targets follow the WordPress staging redirect
  // where it had one.
  deionsanders: "/results/deion-sanders/", // 42 + 16 referring domains
  prp: "/prp-treatment-for-hair-restoration-its-effectiveness-and-all-things-to-know-about-prp-therapy/", // 29, WP target
  "pricing-test": "/pricing/", // 20
  dallas: "/locations/texas/dallas-highland-park/", // 10, WP target
  mancow: "/", // 8, WP target
  "9-ways-encourage-hair-growth": "/10-ways-actually-make-hair-grow/", // 16, WP target
  "reverse-hair-loss": "/10-ways-actually-make-hair-grow/", // 7, WP target
  wise_choice: "/hair-restoration-research-tips/", // 1, WP target
  "the-cost-of-a-hair-transplant-pricing-information": "/the-true-hair-restoration-cost/", // 11
  // Blog posts that duplicate a real page (audit, 3 Oct 2026). The post row
  // stays in Supabase; the URL goes to the page. brian-urlacher-restore also
  // has its own route file; the entry here makes blog links and /llms.txt
  // skip the redirect. dr-james-harris-denver was here until 6 Oct 2026. It
  // ranked at position 8.7 before the move, so it is a page again, with the
  // WordPress copy (src/content/wp-parity/posts/dr-james-harris-denver.md).
  "brian-urlacher-restore": "/results/brian-urlacher/",
  // Old video page, 404 in Cloudflare analytics 3 to 6 Oct 2026. No WP target
  // (staging answers 404). Inference from the slug: it showed the Brian
  // Urlacher video, so it goes to his results page.
  "brian-video": "/results/brian-urlacher/",
  // RESTORE's search cleanup log (Chris Muccio, 16 Oct 2026, Search Console
  // 15 Jul to 13 Oct 2026). Each target answered 200 with no redirect on
  // restorehair.com on 17 Oct 2026. The log's old 404 addresses are in
  // legacy-path-redirects.ts (#129).
  // Duplicate posts: the stronger page keeps the URL. The rows stay in
  // Supabase and are in REDIRECTED_POST_SLUGS (blog-listing.ts).
  "psychology-of-hair-loss-2": "/psychology-of-hair-loss/", // 9 impressions vs 1,524
  "beard-transplants": "/beard-transplant/", // 21 impressions vs 3,140
  // Three Chicago pages split the signals. The log keeps the clinic page,
  // which ranks best for Chicago searches (SpyFu, 17 Oct 2026: "hair
  // transplant chicago" #16 vs #53 for the greater-Chicago post). Neither
  // post is a Google Ads final URL.
  "hair-restoration-in-the-greater-chicago-area": "/locations/restore-chicago/",
  "chicago-hair-restoration": "/locations/restore-chicago/",
  // Competing posts from the same log, merged on 17 Oct 2026 (Anthony
  // Wentzel's decision, Search Console 15 Apr to 14 Oct 2026 and Google Ads
  // landing pages 17 Sep to 16 Oct 2026). Each keeper took the sections only
  // the old post had (src/content/wp-parity/posts/<keeper>.md). The rows stay
  // in Supabase and are in REDIRECTED_POST_SLUGS (blog-listing.ts).
  "evaluating-the-cost-of-a-hair-transplant": "/the-true-hair-restoration-cost/", // 0 clicks vs 144
  // 46 clicks vs 428. Also a Google Ads final URL (about 11 clicks a month):
  // the 301 keeps the query string (withQuery in $slug.tsx).
  "genetics-and-hair-loss-who-do-you-inherit-it-from": "/baldness-gene-come-mother-father/",
  "cure-for-baldness": "/is-there-really-a-cure-for-baldness/", // 0 clicks vs 252
  "best-hair-transplant-results-near-me": "/results/", // 0 clicks, 21 impressions
  "types-of-hair-loss-treatment": "/non-surgical-hair-loss-treatments-at-restore-hair/", // 0 clicks, 17 impressions
  // Old-URL audit, 1 Nov 2026: every WordPress page, post and upload (REST
  // API and sitemaps), the Wayback Machine, Google Ads, Ahrefs, Search Console
  // and RESTORE's emails, checked against restorehair.com. Each of these
  // answered 404 there. "WP" marks the redirect the old WordPress origin
  // (thriverestorehair.kinsta.cloud) still serves; the rest are inferred from
  // the slug or the old page title. Each target answered 200 on 1 Nov 2026.
  // Google Ads final URLs and sitelinks (campaigns paused today, so a
  // re-enabled ad would land on a 404).
  "landing-page-dallas": "/locations/restore-dallas/", // like landing-page-chicago
  "landing-page-denver": "/locations/restore-denver/", // WP
  "pricing-no-shave": "/pricing/", // WP; also 5 sitelink assets
  "results-new2": "/results/", // WP; sitelink
  "schedule-a-c": "/schedule-a-consult/", // WP; sitelink
  // Ads landing page with 78 clicks from May to Oct 2026. On WordPress it was
  // the SMP page, which goes where /smp goes (legacy-path-redirects.ts).
  density: "/non-surgical/",
  // Pages other sites link to (Ahrefs referring domains).
  "pricing-zach": "/pricing/", // 3
  "curtis-video": "/reviews/", // 2; WP sent it to a video file on S3
  "5-unexpected-causes-of-alopecia-and-how-to-treat-hair-loss": "/alopecia-difference/", // 2
  "can-hair-loss-impact-career": "/psychology-of-hair-loss/", // 2
  // Pages in the old WordPress sitemap.
  "restore-vs-other-guys": "/why-restore/",
  "hair-transplant-pricing-what-does-a-hair-transplant-cost": "/pricing/",
  "colorado-brochure": "/downloads/RESTORE-Hair-Brochure.pdf", // WP: /brochure/
  "pdf-flipbook": "/downloads/RESTORE-Hair-Brochure.pdf", // the brochure as a flip book
  wgn: "/media-outlet/", // WP: /media-coverage/wgn/, which is gone too
  // clone-of- pages that held another article's text, sent where WordPress
  // sent them (3 Nov 2026). The other clone-of- slugs go to the page they
  // were cloned from (legacyPageRedirect strips the prefix).
  "clone-of-beard-transplants": "/what-is-dr-james-harriss-role-at-restore/", // 97% same text
  "clone-of-do-hair-plugs-look-natural": "/why-do-some-men-need-multiple-hair-transplant-sessions/", // 99%
  "clone-of-how-do-clinics-ensure-donor-area-preservation-for-future-needs": "/how-long-do-hair-transplant-results-last/", // 99%
  // WordPress served these two articles at clone-of- URLs. They are back at
  // their own slugs (src/lib/local-posts.ts).
  "clone-of-how-soon-will-transplanted-hair-start-to-grow": "/what-questions-should-i-ask-during-a-hair-transplant-consultation/",
  "clone-of-why-do-some-men-need-multiple-hair-transplant-sessions": "/does-restore-use-robotics-in-hair-transplant-surgeries/",
  // Old WordPress test page. Its clone (/clone-of-formidable-test-page-2/)
  // had a route file sending it home; WordPress answered 404 for both.
  "formidable-test-page-2": "/",
};

/**
 * The 301 target for a retired page slug, or null.
 *
 * Also handles Lovable's "clone-of-" duplicates (clone-of-clone-of-x): the
 * prefixes are stripped and the remaining slug is looked up again, falling
 * back to /<slug>/ when it is a live page.
 */
export function legacyPageRedirect(slug: string): string | null {
  // Old links use mixed case, for example /DeionSanders/.
  const direct = LEGACY_PAGES[slug] ?? LEGACY_PAGES[slug.toLowerCase()];
  if (direct) return direct;
  if (!slug.startsWith("clone-of-")) return null;
  const base = slug.replace(/^(clone-of-)+/, "");
  if (!base) return null;
  return LEGACY_PAGES[base] ?? `/${base}/`;
}
